import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import KnowledgeBasedAuthenticationPage from './component';
import {
  getLandlordStatus,
  requestIdVerificationQuestions,
  submitIdVerificationAnswers,
} from '@rental-manager/actions/requests';
import { closeNotification, showModalNotification } from '@common/actions/NotificationActions';
import { DEFAULT_ERROR } from '@rental-manager/constants/NotificationConstants';
import paths from '@rental-manager/pathsManifest';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import { LANDLORD_STATUS } from '@rental-manager/constants/OnboardingConstants';
import { saveExperianErrorState } from '@rental-manager/actions/OnboardingActions';
import { logError } from '@zg-rentals/log-error';

const mapStateToProps = (
  state,
  { propertyId, fromDashboard, setupNewTerm = false, goToConfirmation, goToPrevPage },
) => {
  const { identityVerificationInfo } = state.payments.landlord.onboarding[propertyId];

  return {
    landlordIdentityInfo: {
      firstName: identityVerificationInfo.firstName,
      lastName: identityVerificationInfo.lastName,
      currentAddress: identityVerificationInfo.address ?? identityVerificationInfo.street ?? '',
      currentState: identityVerificationInfo.state ?? '',
      currentCity: identityVerificationInfo.city ?? '',
      currentZip: identityVerificationInfo.zip ?? identityVerificationInfo.zipCode ?? '',
    },
    oneTimePaymentRequest: state.payments.landlord.property.oneTimePaymentRequest,
    fromDashboard,
    setupNewTerm,
    goToConfirmation,
    goToPrevPage,
  };
};

const mapDispatchToProps = (
  dispatch,
  { baseUrl, propertyId, fromDashboard, landlordStatus, setupNewTerm }: Record<string, any>,
) => ({
  requestIdentityVerificationQuestions: (
    landlordIdentityInfo = {
      firstName: '',
      lastName: '',
      currentAddress: '',
      currentState: '',
      currentCity: '',
      currentZip: '',
    },
  ) =>
    Promise.resolve()
      .then(() =>
        dispatch(
          requestIdVerificationQuestions({
            propertyId,
            ...landlordIdentityInfo,
          }),
        ),
      )
      .then(checkRequestActionSuccess)
      .then((action) => action.payload)
      .catch((action = {}) => {
        logError({ error: action });
        return {
          error: true,
          ...action,
        };
      }),
  submitIdentityVerificationAnswers: (answers) =>
    Promise.resolve()
      .then(() => dispatch(submitIdVerificationAnswers({ propertyId, answers })))
      .then(checkRequestActionSuccess)
      .then((action = {}) => action.payload)
      .catch((action = {}) => {
        logError({ error: action });
        return { error: true };
      }),
  onIdentityVerificationSuccess: async () => {
    // Request the latest landlord status
    const landlordStatusResponse = await dispatch(getLandlordStatus({ propertyId, setupNewTerm }));
    await checkRequestActionSuccess(landlordStatusResponse);
    if (setupNewTerm) {
      return {
        success: true,
      };
    }
    if (fromDashboard) {
      return dispatch(push(baseUrl + paths.dashboard.confirmMoveInCostsPage));
    }
    return dispatch(push(baseUrl + paths.onboarding.confirmationPage));
  },
  saveExperianErrorState: (identityCheckResponse) =>
    Promise.resolve()
      .then(() => dispatch(saveExperianErrorState({ propertyId, identityCheckResponse })))
      .then(() => ({ success: true }))
      .catch((err) => {
        logError({ error: err });
        dispatch(showModalNotification(DEFAULT_ERROR));
        return { error: true };
      }),
  onBack: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        const initialVerificationComplete =
          landlordStatus !== LANDLORD_STATUS.NOT_STARTED &&
          landlordStatus !== LANDLORD_STATUS.ONBOARDING_STARTED &&
          landlordStatus !== LANDLORD_STATUS.EXTERNAL_ACCOUNT_SET;

        if (fromDashboard) {
          return dispatch(push(baseUrl));
        } else if (initialVerificationComplete) {
          return dispatch(push(baseUrl + paths.onboarding.inviteTenantPage));
        }
        return dispatch(push(baseUrl + paths.onboarding.taxVerificationPage));
      }),
  onClose: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        if (fromDashboard) {
          return Promise.resolve()
            .then(() => dispatch(push(baseUrl)))
            .then(() => dispatch(getLandlordStatus({ propertyId })));
        }
        return dispatch(push(baseUrl + paths.onboarding.exitPaymentSetup));
      }),
  showRequestErrorNotification: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(showModalNotification(DEFAULT_ERROR))),
});

const mergeProps = (stateProps, dispatchProps) => ({
  ...stateProps,
  ...dispatchProps,
  requestIdentityVerificationQuestions: () => {
    const { landlordIdentityInfo } = stateProps;
    return dispatchProps.requestIdentityVerificationQuestions(landlordIdentityInfo);
  },
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(KnowledgeBasedAuthenticationPage);
